import { Fragment, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import classNames from 'classnames'

import ADND from '@aytm/lib/adnd/ADND'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { getReportId, isReportStorageDisabled } from '@aytm/app/stats/lib/stat-page'

// import { Storage } from '@aytm/lib/storage/index'

// import { submitHiddenForm } from '@aytm/app/stats/lib/util/dom'
import { customAnswersTitle, formatAmounts } from '@aytm/app/stats/lib/util/helpers'
// import { getCSRFParam, getCSRFToken } from '@aytm/lib/common/rails-post-fetch'
import { getQuestionId, getQuestionIcon } from '@aytm/app/stats/page/components/lib/utils'
import { getIsDefaultReportParams } from '@aytm/app/stats/page/comments/lib/utils'

import htmlReactParser, { domToReact } from 'html-react-parser'
import openSidebarLink, {
  OPEN_SIDEBAR_LINK_NAME,
  CROSSTAB_SIDEBAR_TAB,
  checkSidebarLink
} from '@aytm/app/stats/page/question-views/components/lib/skipper-links/open-sidebar'

// components
// import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import QuestionTitle from '@aytm/app/stats/page/question-views/components/QuestionTitle'
import QuestionCrosstab from '@aytm/app/stats/page/question-views/components/QuestionCrosstab'
import QuestionAnnotation from '@aytm/app/stats/page/question-views/components/QuestionAnnotation'

import ImagePopup from '@aytm/app/stats/components/image-popup/ImagePopup'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

import { trimNote, toDraft, draftsMatch } from '@aytm/app/stats/page/question-views/components/lib/drafts/draftRecord'

// hooks
import useMountedRef from '@aytm/lib/hooks/useMountedRef'
import useSingleAndDoubleClick from '@aytm/app/stats/page/components/hooks/useSingleAndDoubleClick'
import useEditDraft, { logDraft } from '@aytm/app/stats/page/question-views/components/lib/drafts/useEditDraft'

// consts
import {
  QUESTION_TITLE_THUMB_WIDTH,
  QUESTION_TITLE_THUMB_HEIGHT,
  TYPE_DND_MAP
} from '@aytm/app/stats/page/question-views/components/lib/constants'

// SVGs
// import RadarSVG from '@aytm/app/stats/page/question-views/components/lib/svg/radar.svg'
import { questionPositionToVP } from '@aytm/app/stats/page/components/lib/utils'

// styles
import style from '@aytm/app/stats/page/question-views/components/styles/QuestionHeader.module.scss'
import annotationStyle from '@aytm/app/stats/page/question-views/components/styles/QuestionAnnotation.module.scss'
import { isSharedView } from '@aytm/app/stats/lib/page/viewer'

const QuestionHeader = ({
  className,
  model = {},
  question,
  onScrollToSkip,
  reportId,
  features,
  sortableList,
  questionView,
  isEditingExt,
  titleExt,
  isQuestionCollapsed,
  // isReportXpert,
  isShared = isSharedView(),
  // getReportParams,
  onToggleCollapse,
  onUpdateParams,
  onTitleChange,
  onStorytellingNoteChange,
  oeSearchParams,
  onCloneQuestion,
  onAIAssistantsClick,
  onAIAddQuestionClick,
  onFinalizeDragLayerContent,
  onDndStart,
  onDndEnd,
  onSetTitleExt,
  onSetEditQuestionExt,
  onInterruptEditingExt,
  onEditingChange,
  onSaveExt,
  hasDraftExt,
  onAIAssistantsCancel,
  onRemoveQuestion,
  getCrosstabData
}) => {
  const {
    id,
    key,
    schemaKey,
    removed,
    parentID,
    icon,
    type,
    pre_type,
    subType,
    position,
    visual_position,
    text,
    header,
    storytellingNote,
    answer,
    image,
    skipsTo,
    aiAssistantsHash,
    aiProgressMessage,
    disabled
  } = model

  const [title, setTitle] = useState(
    titleExt != undefined && null !== titleExt ? titleExt : ('string' == typeof header && header) || text
  )
  const [note, setNote] = useState(storytellingNote)

  const [isEditing, setIsEditing] = useState(isEditingExt || false)
  const [isCollapsed, setIsCollapsed] = useState(isQuestionCollapsed)
  const [isCrosstabCollapsed, setIsCrosstabCollapsed] = useState(true)
  const [thumbnailPreviewOpened, setThumbnailPreviewOpened] = useState(false)

  const [aiAssistantCrosstab, setAIAssistantCrosstab] = useState({ overLimit: false })
  const [aiAssistantPopupPosition, setAIAssistantPopupPosition] = useState('topright')
  const [aiAssistantRequestFailed, setAIAssistantRequestFailed] = useState(false)

  const mountedRef = useMountedRef()

  const componentRef = useRef(null)

  const savedValues = useRef(null)
  savedValues.current = {
    title: titleExt != undefined && null !== titleExt ? titleExt : ('string' == typeof header && header) || text,
    note: storytellingNote
  }

  // the annotation in its saved form; the note state holds the editing form while the editor is open
  const savedFormNote = useRef(storytellingNote || '')
  // read by callbacks and effects that can run after the editor has closed
  const editingRef = useRef(false)
  editingRef.current = Boolean(isEditing)

  const dndListItemRef = useRef(questionView)
  const dndListItemHandleRef = useRef()

  // const isRadarEnabled =
  //   ['rb', 'cb', 'mx', 'sr', 'se', 'ro', 'aro', 'mp', 'qa', 'bpm', 'psc'].includes(type) && !features.radar.hidden

  const isAIAssistantsEnabled = !isShared && !disabled && !features.ai_assistants_stat_summary.disabled

  // The crosstab link asks for this question's own banner bar rather than the export panel's copy,
  // so it opens the head's crosstab instead of switching the sidebar.
  const openQuestionCrosstab = useCallback(() => void setIsCrosstabCollapsed(false), [])
  const onOpenCrosstab = isAIAssistantsEnabled ? openQuestionCrosstab : null

  const aiProgressMessageNode = useMemo(
    () =>
      aiProgressMessage
        ? htmlReactParser(aiProgressMessage, {
            trim: true,
            replace({ name, attribs, children }) {
              if (OPEN_SIDEBAR_LINK_NAME == name) {
                const tab = attribs?.tab || ''
                const focus = attribs?.focus || ''
                const icon = attribs?.icon || ''
                const isCrosstab = CROSSTAB_SIDEBAR_TAB == tab

                return (isCrosstab ? Boolean(onOpenCrosstab) : checkSidebarLink({ tab })) ? (
                  <span
                    className={`${annotationStyle.annotationLink} -dtr-open-sidebar-link -dtr-open-sidebar-link-${tab} -dtr-open-sidebar-link-${focus}`}
                    {...pressable(isCrosstab ? onOpenCrosstab : () => void openSidebarLink({ tab, focus }))}
                  >
                    {icon ? <i className={`${icon} ${annotationStyle.annotationLinkIcon}`} /> : null}
                    {domToReact(children)}
                  </span>
                ) : (
                  <span className={`${annotationStyle.annotationLink} ${annotationStyle.annotationLinkDisabled}`}>
                    {icon ? <i className={`${icon} ${annotationStyle.annotationLinkIcon}`} /> : null}
                    {domToReact(children)}
                  </span>
                )
              }
              return void 0
            }
          })
        : '',
    [aiProgressMessage, onOpenCrosstab]
  )

  const isTitleEditable =
    !isShared && !disabled && ('function' == typeof onTitleChange || 'function' == typeof onSaveExt)
  const isNoteEditable = !isShared && !disabled && 'function' == typeof onStorytellingNoteChange
  const isAnnotationEditable = !isShared && !disabled && !features.disabled

  // a chart question's header runs the edit and its draft; a storytelling note runs both itself
  const ownsEdit = 'function' != typeof onSetEditQuestionExt
  // a title nothing can save stays out of the draft
  const draftsTitle = 'function' == typeof onTitleChange
  const savedTitle = ('string' == typeof header && header) || text

  const typedDraft = () => toDraft({ text: draftsTitle ? title : '', note: savedFormNote.current })
  const savedDraft = toDraft({ text: draftsTitle ? savedTitle : '', note: storytellingNote })

  const surveyId = PageGlobals.SURVEY_ID
  // the report the annotation is saved on, which is not always the one reportId names
  const draftReportId = getReportId()

  const editDraft = useEditDraft({
    kind: 'annotation',
    surveyId,
    userId: PageGlobals.USER_ID || 0,
    reportId: draftReportId,
    id: schemaKey,
    enabled: ownsEdit && Boolean(schemaKey) && surveyId != undefined && draftReportId != undefined,
    editing: Boolean(isEditing),
    removed: Boolean(removed),
    paused: !isAnnotationEditable,
    memoryOnly: isReportStorageDisabled(),
    typed: typedDraft,
    saved: savedDraft
  })
  const hasDraft = ownsEdit ? editDraft.hasDraft : hasDraftExt

  const isNoteDisabled = isEditing && (!title.trim().length || draftsMatch(typedDraft(), savedDraft))

  // a note on show carries the pencil itself, so the head only keeps it while there is no note to sit on
  const isNoteShown = !isEditing && !isCollapsed && !aiProgressMessage && !onSaveExt && Boolean(note)

  const isTogglable =
    !thumbnailPreviewOpened && !isEditing && !features.toggling.disabled && 'function' == typeof onToggleCollapse
  const isDraggable = !thumbnailPreviewOpened && !isEditing && !disabled && !features.reorder_questions.disabled
  const isCloneable =
    !position && !thumbnailPreviewOpened && !isEditing && !disabled && 'function' == typeof onCloneQuestion

  const questionId = useMemo(
    () => getQuestionId({ id, type, pre_type, position, visual_position }, parentID).toUpperCase(),
    [id, parentID, type, pre_type, position, visual_position]
  )

  const questionIcon = useMemo(
    () =>
      getQuestionIcon({
        type: `${icon || type}${(model.features != undefined && model.features.insightsMapping && '-mapping') || ''}`,
        pre_type,
        subType
      }),
    [icon, type, pre_type, subType, model.features]
  )

  const onShowCommentsPage = useCallback(
    (event, mode) => {
      event.preventDefault()

      const query = []
      const selectedQuestion = questionId.toLowerCase()
      const searchParams = oeSearchParams

      // the video page is always survey-wide, so its slot never keeps a question
      searchParams.selectedQuestion = 'video-responses' != mode ? selectedQuestion : ''
      searchParams.query = query

      const params = {}
      params[mode] = getIsDefaultReportParams(searchParams, mode) ? null : searchParams

      onUpdateParams(event, params, reportId, mode)
    },
    [questionId, reportId, oeSearchParams, onUpdateParams]
  )

  // const onShowRadarPage = useCallback(
  //   event => {
  //     event.preventDefault()

  //     if (isReportXpert || !getReportParams) return
  //     const params = getReportParams()

  //     const justFiltersParams = {}
  //     Object.keys(params).forEach(pk => {
  //       const pv = params[pk]
  //       if (/^(filter|virtual|schema|psc\d+)/.test(pk)) {
  //         justFiltersParams[pk] = pv
  //       }
  //     })
  //     if (id) justFiltersParams['q'] = Number(id)

  //     const meta = { [getCSRFParam()]: getCSRFToken() }
  //     const url = getStatUrl(
  //       window.shareKey && window.shareKey.key ? `stat/${window.shareKey.key}/radar` : 'radar',
  //       true
  //     )

  //     Storage.Encode(justFiltersParams).then(base64params => {
  //       meta._bsfz = base64params
  //       submitHiddenForm(url, meta)
  //     })
  //   },
  //   [id, isReportXpert, getReportParams]
  // )

  const dndType =
    0 < position
      ? `question-${PageGlobals.WINDOW_UID}`
      : `${TYPE_DND_MAP[type]}${model.features != undefined && model.features.insightsMapping ? '-mapping' : ''}`

  const dndListItem = isDraggable
    ? ADND.targets.itemsList
        .listItem({
          type: dndType,
          list: sortableList
        })
        .onFinalizeDragLayerContent(onFinalizeDragLayerContent)
    : null

  const dndListItemHandle = isDraggable
    ? ADND.sources
        .listItemHandle({
          type: dndType,
          item: dndListItem,
          lockX: false,
          cloneable: isCloneable
        })
        .onStart(() => onDndStart(dndType))
        .onEnd(onDndEnd)
        .fetchData(() => {
          return {
            key,
            model: question,
            windowUID: PageGlobals.WINDOW_UID
          }
        })
    : null

  const onHeaderSingleClick = () => {
    if (!isTogglable) return
    onToggleCollapse()
  }

  const onHeaderDoubleClick = () => {
    if (!isCloneable) return
    onCloneQuestion()
  }

  const onChangeNote = (html, savedHtml) => {
    // the rich-text editor reports keystrokes late, so a change can arrive after the editor has closed
    if (!editingRef.current) return
    // the ref first: the editor reports outside React's batching, so setNote renders before the next line runs
    savedFormNote.current = savedHtml ?? html ?? ''
    setNote(html)
  }

  // the host takes an open editor down with the header on a report switch
  const setOwnEditing = editing => {
    setIsEditing(editing)
    if ('function' == typeof onEditingChange) onEditingChange(editing)
  }

  const startOwnEdit = () => {
    const draft = editDraft.read()
    if (draft) {
      logDraft('onStartEditing annotation:', schemaKey)
      logDraft('onStartEditing draft:', draft)
      draftsTitle && setTitle(draft.text)
      setNote(draft.note)
    }
    savedFormNote.current = draft ? draft.note : storytellingNote || ''
    setOwnEditing(true)
  }

  const closeOwnEditor = () => {
    setTitle(savedValues.current.title)
    setNote(savedValues.current.note)
    setOwnEditing(false)
  }

  const onCancelEditing = () => {
    if ('function' == typeof onSetEditQuestionExt) {
      onSetEditQuestionExt(false)
    } else {
      editDraft.discard('CANCEL')
      closeOwnEditor()
    }
  }

  // a collapse or an AI job ends the edit without discarding it
  const onInterruptEditing = () => {
    if ('function' == typeof onInterruptEditingExt) return void onInterruptEditingExt()
    if (!ownsEdit) return void onCancelEditing()
    if (!isEditing) return
    editDraft.keep()
    closeOwnEditor()
  }

  const onSaveEditing = (withThisNote = savedFormNote.current) => {
    if (isNoteEditable && (storytellingNote != withThisNote || note != withThisNote)) {
      const updatedNote = trimNote(withThisNote)
      setNote(updatedNote)
      onStorytellingNoteChange(updatedNote)
    }

    if (isTitleEditable && title != savedTitle) {
      onTitleChange(title)
    }

    if ('function' == typeof onSetEditQuestionExt) {
      onSetEditQuestionExt(false)
    } else {
      editDraft.discard('SAVE')
      setOwnEditing(false)
    }
  }

  const onStartEditing = event => {
    event.preventDefault()

    if (isCollapsed) onHeaderSingleClick()
    if ('function' == typeof onSetEditQuestionExt) {
      onSetEditQuestionExt(true)
    } else {
      startOwnEdit()
    }
  }

  const onSkipToClick = (event, position) => {
    event.preventDefault()

    // EXIT and HIDDEN EXIT are ends of the survey, not questions -- the single-target form draws
    // them through this same span, so there is nowhere for the page to scroll
    if (!(0 < position)) return
    if ('function' == typeof onScrollToSkip) {
      onScrollToSkip(position)
    }
  }

  const onKeyDownHandler = event => {
    if (27 == event.keyCode) {
      onCancelEditing()
    }
  }

  const onHeaderClick = useSingleAndDoubleClick(
    isTogglable ? onHeaderSingleClick : null,
    isCloneable ? onHeaderDoubleClick : null
  )

  const onScroll = () => {
    if (null === componentRef.current) {
      setAIAssistantPopupPosition('topright')
    } else {
      const stickyHeader = document.querySelector('.stats-StickyHeader')

      if (stickyHeader) {
        const header = document.querySelector('.-header')
        const headerRect = header.getBoundingClientRect()

        const stickyHeaderRect = stickyHeader.getBoundingClientRect()

        const componentRect = componentRef.current.getBoundingClientRect()
        setAIAssistantPopupPosition(
          componentRect.y < headerRect.y + headerRect.height + stickyHeaderRect.height + 220
            ? 'bottomright'
            : 'topright'
        )
      }
    }
  }

  const onAIAssistantsClickCallback = data => {
    if (mountedRef.current) {
      const failed = null === data || 'failed' == data.scope?.aiStatus || (!data.response && !data.scope?.summary)

      setAIAssistantRequestFailed(failed)
      // the notice hangs off the Explore button, so a failure that lands on a closed bar opens it
      if (failed) setIsCrosstabCollapsed(false)
    }
  }

  useEffect(() => {
    if (aiAssistantRequestFailed) {
      document.addEventListener('scroll', onScroll, false)
      document.defaultView.addEventListener('resize', onScroll, false)
    }
    onScroll()

    return () => {
      document.removeEventListener('scroll', onScroll, false)
      document.defaultView.removeEventListener('resize', onScroll, false)
    }
  }, [aiAssistantRequestFailed])

  useEffect(() => {
    if (isEditing) window.addEventListener('keydown', onKeyDownHandler)
    else window.removeEventListener('keydown', onKeyDownHandler)
    return () => {
      window.removeEventListener('keydown', onKeyDownHandler)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing])

  useEffect(() => {
    // the typed annotation wins while the header's own editor is open; every way of closing it resets from the saved one
    if (ownsEdit && editingRef.current) return
    if (note != storytellingNote) {
      setNote(storytellingNote)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storytellingNote])

  // the pencil going away mid-edit closes the editor and keeps the typed state as the draft
  useEffect(() => {
    if (ownsEdit && !isAnnotationEditable) onInterruptEditing()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnnotationEditable])

  useEffect(() => {
    if (isEditing && isQuestionCollapsed) {
      onInterruptEditing()
    }
    setIsCollapsed(isQuestionCollapsed)

    if (isQuestionCollapsed && !isCrosstabCollapsed) setIsCrosstabCollapsed(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuestionCollapsed])

  useEffect(() => {
    setTitle(
      titleExt != undefined && null !== titleExt
        ? titleExt
        : isEditing
        ? title
        : ('string' == typeof header && header) || text
    )
  }, [titleExt, text, header, title, isEditing])

  useEffect(() => {
    if (null !== isEditingExt) {
      setIsEditing(isEditingExt)
    }
  }, [isEditingExt])

  useEffect(() => {
    if (null !== aiAssistantsHash) {
      onInterruptEditing()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiAssistantsHash])

  useEffect(() => {
    if (dndListItem) dndListItem.connect(dndListItemRef.current)

    return () => {
      if (dndListItem) dndListItem.disconnect()
    }
  }, [dndListItem])

  useEffect(() => {
    if (dndListItemHandle) dndListItemHandle.connect(dndListItemHandleRef.current)

    return () => {
      if (dndListItemHandle) dndListItemHandle.disconnect()
    }
  }, [dndListItemHandle])

  useEffect(() => {
    if (dndListItemHandle)
      dndListItemHandle.fetchData(() => {
        return {
          key,
          model: question,
          windowUID: PageGlobals.WINDOW_UID
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question])

  const renderQuestionThumbnail = () => (
    <div className={style.thumbnail}>
      <ImagePopup
        image={image}
        width={QUESTION_TITLE_THUMB_WIDTH}
        height={QUESTION_TITLE_THUMB_HEIGHT}
        fill
        onImageOpen={() => setThumbnailPreviewOpened(true)}
        onImageHide={() => setThumbnailPreviewOpened(false)}
      />
    </div>
  )

  const renderAnnotationEditButton = inNote => (
    <span
      id={`stat-annotation-edit-button-${id}`}
      className={classNames('stat-annotation-edit-button -dtr-stat-annotation-edit-button', style.titleEdit, {
        [style['titleEdit-disabled']]: aiAssistantsHash,
        [style['titleEdit-inNote']]: inNote,
        [style['titleEdit-draft']]: hasDraft && !aiAssistantsHash
      })}
      aria-label={note ? 'Edit the storytelling note' : 'Add a storytelling note'}
      title={
        hasDraft && !aiAssistantsHash
          ? 'This note has unsaved changes'
          : note
          ? 'Edit the storytelling note'
          : 'Add a storytelling note'
      }
      {...pressable(aiAssistantsHash ? null : onStartEditing)}
    >
      <i className={note ? 'icon-aytmfd-edit-SN' : 'icon-aytmfd-add-SN'} />
    </span>
  )

  // const renderRadarLink = () => {
  //   const msg = features.radar.available ? (
  //     <>
  //       {/* PME2 (AYTM-11321): render server-authored `text` verbatim; guard the icon, which the
  //           already-on-Enterprise Contact-Us variant omits. */}
  //       {features.radar.onPackage.icon ? (
  //         <span
  //           className={classNames('stat-upgrade-icon', {
  //             [features.radar.onPackage.id]: true
  //           })}
  //         >
  //           <i className={`icon-aytmfd-${features.radar.onPackage.icon}`} />
  //         </span>
  //       ) : null}
  //       <span dangerouslySetInnerHTML={{ __html: features.radar.onPackage.text }} />
  //     </>
  //   ) : features.radar.disabled ? (
  //     <>
  //       Please login/join the team to unlock all features
  //       <br />
  //       of the stat report
  //     </>
  //   ) : null

  //   return (
  //     <div
  //       className={classNames(style.radar, {
  //         [style.disabled]: features.radar.disabled
  //       })}
  //     >
  //       <Popup
  //         width="254px"
  //         trigger={null !== msg ? 'hover' : 'none'}
  //         direction="bottomright"
  //         popupOffset={9}
  //         show={null !== msg ? null : false}
  //         hideOtherPopupsOnShow
  //         content={() => <div>{msg}</div>}
  //       >
  //         {features.radar.available ? (
  //           <a className={style.aRadar} href={features.radar.onPackage.upgradeURL} rel="noreferrer">
  //             <img src={RadarSVG} alt="Radar" />
  //           </a>
  //         ) : (
  //           <span className={style.aRadar} onClick={features.radar.disabled ? null : onShowRadarPage}>
  //             <img src={RadarSVG} alt="Radar" />
  //           </span>
  //         )}
  //       </Popup>
  //     </div>
  //   )
  // }

  const renderSkipToLinks = () => {
    let res
    if (skipsTo.length > 1) {
      const qSkipsLinks = skipsTo.map(qSkipTo => {
        let item
        if (qSkipTo > 0) {
          item = (
            <span key={`skipTo${qSkipTo}`} {...pressable(event => onSkipToClick(event, qSkipTo))}>
              {questionPositionToVP(qSkipTo)}
            </span>
          )
        } else if (-1 == qSkipTo) {
          item = 'EXIT'
        } else if (-99 == qSkipTo) {
          item = 'HIDDEN EXIT'
        }
        return item
      })
      res = (
        <>
          <span className={style.aSkipTo}>
            <i className="icon-aytmfd-random" />
          </span>
          <span className={style.spanSkipTo}>
            (
            {qSkipsLinks.map((qSkipLink, idx) => (
              <Fragment key={`skipToLink${idx}`}>
                {idx ? ' or ' : null}
                {qSkipLink}
                {0 == (idx + 1) % 3 && qSkipsLinks.length > idx + 1 ? <br /> : null}
              </Fragment>
            ))}
            )
          </span>
        </>
      )
    } else {
      const skipToText =
        -1 == skipsTo[0] ? 'EXIT' : -99 == skipsTo[0] ? 'HIDDEN EXIT' : questionPositionToVP(skipsTo[0])
      res = (
        <>
          <span
            className={style.aSkipTo}
            aria-label={`Skip to ${skipToText}`}
            {...pressable(event => onSkipToClick(event, skipsTo[0]))}
          >
            <i className="icon-aytmfd-skip" />
          </span>
          <span className={style.spanSkipTo} {...pressable(event => onSkipToClick(event, skipsTo[0]))}>
            {skipToText}
          </span>
        </>
      )
    }

    return (
      <div
        className={classNames(style.skipTo, {
          [style.random]: skipsTo.length > 1,
          [style.exit]: 1 == skipsTo.length && skipsTo[0] < 0
        })}
      >
        {res}
      </div>
    )
  }

  // The mark opens Skipper's settings rather than asking outright: the request is made from the
  // Explore button down there, beside the banners it will run against.
  const renderAIAssistantsButton = () => (
    <span
      data-skipper-question-button={questionId}
      className={classNames(
        'stat-skipper-question-button',
        '-dtr-stat-skipper-question-button',
        `-dtr-stat-skipper-question-button--${type || 'unknown'}`,
        // The mark reports only whether the settings are open. It used to spin its own wheel while
        // Skipper thought; the wheel that says that is the larger one on the Explore button now, and
        // this one holds still rather than turning back and forth beside it.
        { [style['aiAssistants-open']]: !isCrosstabCollapsed }
      )}
      aria-label="Explore with Skipper"
      aria-expanded={!isCrosstabCollapsed}
      title={isCrosstabCollapsed ? 'Explore with Skipper' : 'Close Skipper settings'}
      {...pressable(() => void (isCollapsed && onHeaderSingleClick(), setIsCrosstabCollapsed(!isCrosstabCollapsed)))}
    >
      <i className="icon-aytmfd-skipper" />
    </span>
  )

  const onRemoveKeyDown = event => {
    if ('Enter' !== event.key && ' ' !== event.key) return

    event.preventDefault()
    onRemoveQuestion?.()
  }

  const renderAIAssistants = () =>
    isAIAssistantsEnabled ? <div className={style.aiAssistants}>{renderAIAssistantsButton()}</div> : null

  return (
    <>
      <div ref={componentRef} className={classNames(style.container, className)}>
        {features.excluded_questions.disabled || disabled ? null : (
          <span
            data-testid="stat-question-remove"
            className={style.questionRemove}
            role="button"
            aria-label="Remove question"
            tabIndex={0}
            onClick={onRemoveQuestion}
            onKeyDown={onRemoveKeyDown}
          >
            <i className="icon-aytmfd-x-v2" />
          </span>
        )}

        <div
          className={classNames(style.component, { [style.togglable]: isTogglable, [style.draggable]: isDraggable })}
        >
          {image != undefined && image.original != undefined ? renderQuestionThumbnail() : null}

          <div className={style.subcomponent} onClick={onHeaderClick}>
            <div className={style.questionType}>
              <i className={`icon-aytmfd-${questionIcon}`} />
            </div>
            <div className={style.questionId}>{questionId}</div>

            <div className={style.questionTitleContainer}>
              <QuestionTitle
                question={model}
                title={title}
                editing={isEditing}
                disabled={!isTitleEditable}
                setTitle={'function' == typeof onSetTitleExt ? onSetTitleExt : setTitle}
              />
            </div>
          </div>

          {isAnnotationEditable && !isEditing && !isNoteShown ? renderAnnotationEditButton(false) : null}

          {renderAIAssistants()}

          {skipsTo != undefined && skipsTo.length ? renderSkipToLinks() : null}

          {['oe', 'mn'].includes(type) && !features.comments_page.hidden ? (
            <span
              id={`comments_${position}`}
              className={classNames(style.comments, {
                [style['comments-disabled']]: 'loading' == answer || !answer.comments || features.comments_page.disabled
              })}
              title={
                !features.comments_page.disabled
                  ? 'loading' != answer && null !== answer.comments && 0 < answer.comments
                    ? customAnswersTitle(answer.comments, formatAmounts(answer, 'comments'))
                    : 'No custom answers'
                  : ''
              }
              {...pressable(
                'loading' != answer &&
                  null !== answer.comments &&
                  0 < answer.comments &&
                  !features.comments_page.disabled
                  ? event => onShowCommentsPage(event, 'comments')
                  : null
              )}
            >
              {'loading' != answer && null !== answer.comments && 0 < answer.comments
                ? formatAmounts(answer, 'comments')
                : 0}
              <i className="icon-aytmfd-comments-v2" />
            </span>
          ) : null}

          {'cn' == type && !features.comments_page.hidden ? (
            <span
              id={`comments_${position}`}
              className={classNames(style.comments, {
                [style['comments-disabled']]: 'loading' == answer || !answer.comments || features.comments_page.disabled
              })}
              title={
                !features.comments_page.disabled
                  ? 'loading' != answer && null !== answer.comments && 0 < answer.comments
                    ? customAnswersTitle(answer.comments, formatAmounts(answer, 'comments'))
                    : 'No custom answers'
                  : ''
              }
              {...pressable(
                'loading' != answer &&
                  null !== answer.comments &&
                  0 < answer.comments &&
                  !features.comments_page.disabled
                  ? event => onShowCommentsPage(event, 'comments')
                  : null
              )}
            >
              {'loading' != answer && null !== answer.comments && 0 < answer.comments
                ? formatAmounts(answer, 'comments')
                : 0}
              <i className="icon-aytmfd-comments-v2" />
            </span>
          ) : null}

          {'ir' == type && !features.comments_page.hidden ? (
            <span
              id={`image_responses_${position}`}
              className={classNames(style.imageResponses, {
                [style['imageResponses-disabled']]:
                  'loading' == answer || !answer.images || features.comments_page.disabled
              })}
              title={
                !features.comments_page.disabled
                  ? 'loading' != answer && null !== answer.images && 0 < answer.images
                    ? `View ${formatAmounts(answer, 'images')} image responses`
                    : 'No image responses'
                  : ''
              }
              {...pressable(
                'loading' != answer && null !== answer.images && 0 < answer.images && !features.comments_page.disabled
                  ? event => onShowCommentsPage(event, 'image-responses')
                  : null
              )}
            >
              {'loading' != answer && null !== answer.images && 0 < answer.images ? formatAmounts(answer, 'images') : 0}
              <i className="icon-aytmfd-image-responses-v2" />
            </span>
          ) : null}

          {'va' == type && !features.comments_page.hidden ? (
            <span
              id={`videos_${position}`}
              className={classNames(style.videoResponses, {
                [style['videoResponses-disabled']]:
                  'loading' == answer || !answer.videos || features.comments_page.disabled
              })}
              title={
                !features.comments_page.disabled
                  ? 'loading' != answer && null !== answer.videos && 0 < answer.videos
                    ? `View ${formatAmounts(answer, 'videos')} videos`
                    : 'No videos responses'
                  : ''
              }
              {...pressable(
                'loading' != answer && null !== answer.videos && 0 < answer.videos && !features.comments_page.disabled
                  ? event => onShowCommentsPage(event, 'video-responses')
                  : null
              )}
            >
              {'loading' != answer && null !== answer.videos && 0 < answer.videos ? formatAmounts(answer, 'videos') : 0}
              <i className="icon-aytmfd-video-responses-v2" />
            </span>
          ) : null}

          {/* {isRadarEnabled ? renderRadarLink() : null} */}
        </div>

        {isDraggable && <div ref={dndListItemHandleRef} className={style.questionMove} onClick={onHeaderClick} />}
      </div>
      {isCollapsed ? null : (
        <>
          {/* the bar stays mounted while Skipper thinks: the wheel that says so now turns on the
              Explore button inside it, not on the mark in the head */}
          {isAIAssistantsEnabled && !isCrosstabCollapsed ? (
            <QuestionCrosstab
              getCrosstabData={getCrosstabData}
              onCrosstabChange={setAIAssistantCrosstab}
              isThinking={Boolean(aiAssistantsHash)}
              requestFailed={aiAssistantRequestFailed}
              popupDirection={aiAssistantPopupPosition}
              onDismissFailure={() => void setAIAssistantRequestFailed(false)}
              onExplore={() =>
                void (setAIAssistantRequestFailed(false),
                onAIAssistantsClick(aiAssistantCrosstab, onAIAssistantsClickCallback))
              }
            />
          ) : null}

          {aiProgressMessage ? (
            <div className={style.aiProgressMessage}>
              {aiProgressMessageNode}
              {onAIAssistantsCancel ? (
                <>
                  <br />
                  <br />
                  <div className={style.aiCancelButton} {...pressable(() => void onAIAssistantsCancel())}>
                    Cancel
                  </div>
                </>
              ) : null}
            </div>
          ) : !onSaveExt ? (
            <QuestionAnnotation
              id={id}
              note={note}
              editButton={isAnnotationEditable ? renderAnnotationEditButton(true) : null}
              onStartEditing={isAnnotationEditable && !aiAssistantsHash ? onStartEditing : null}
              editing={isEditing}
              isNoteEditable={isNoteEditable}
              disabled={isNoteDisabled}
              setNote={onChangeNote}
              onSaveEditing={onSaveEditing}
              onCancelEditing={onCancelEditing}
              onOpenCrosstab={onOpenCrosstab}
              onAIAddQuestionClick={onAIAddQuestionClick}
            />
          ) : null}
        </>
      )}
    </>
  )
}

QuestionHeader.propTypes = {
  className: PropTypes.string,
  model: PropTypes.shape({
    id: PropTypes.string,
    key: PropTypes.string,
    schemaKey: PropTypes.string,
    removed: PropTypes.bool,
    parentID: PropTypes.string,
    disabled: PropTypes.bool,
    type: PropTypes.string,
    subType: PropTypes.string,
    pre_type: PropTypes.number,
    position: PropTypes.number,
    visual_position: PropTypes.number,
    header: PropTypes.string,
    text: PropTypes.string,
    textAppendix: PropTypes.string,
    answer: answerPropType,
    image: PropTypes.object,
    skipsTo: PropTypes.array,
    aiProgressMessage: PropTypes.string
  }),
  question: PropTypes.object,
  // isReportXpert: PropTypes.bool,
  isShared: PropTypes.bool,
  reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sortableList: PropTypes.object,
  features: PropTypes.object,
  questionView: PropTypes.any,
  isQuestionCollapsed: PropTypes.bool,
  isEditingExt: PropTypes.bool,
  titleExt: PropTypes.string,
  // getReportParams: PropTypes.func,
  oeSearchParams: PropTypes.object,
  onScrollToSkip: PropTypes.func,
  onToggleCollapse: PropTypes.func,
  onUpdateParams: PropTypes.func,
  onCloneQuestion: PropTypes.func,
  onAIAssistantsClick: PropTypes.func,
  onAIAddQuestionClick: PropTypes.func,
  onFinalizeDragLayerContent: PropTypes.func,
  onDndStart: PropTypes.func,
  onDndEnd: PropTypes.func,
  onTitleChange: PropTypes.func,
  onStorytellingNoteChange: PropTypes.func,
  onSetEditQuestionExt: PropTypes.func,
  onInterruptEditingExt: PropTypes.func,
  onEditingChange: PropTypes.func,
  hasDraftExt: PropTypes.bool,
  onSaveExt: PropTypes.func,
  onSetTitleExt: PropTypes.func,
  onAIAssistantsCancel: PropTypes.func,
  onRemoveQuestion: PropTypes.func,
  getCrosstabData: PropTypes.func
}

QuestionHeader.displayName = 'QuestionHeader'

export default QuestionHeader

ADND().connect()
